import { Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import PaymentsIcon from "@mui/icons-material/Payments";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { formatCurrency, formatInteger } from "../../../../../formatters";
import { PerformanceRow } from "../../../../../Performance/performanceApiMapping";

const PRIMARY = "#8E24AA";
const POSITIVE = "#2E7D32";
const NEGATIVE = "#C62828";

function sum(rows: PerformanceRow[], field: keyof PerformanceRow): number {
  return rows.reduce((total, row) => total + (row[field] as number), 0);
}

interface PerformanceKpiCardsProps {
  rows: PerformanceRow[];
}

// Mobile-specific 2-per-row KPI grid (mockup: 2x2), unlike the desktop
// PerformanceKpiCards.tsx which stacks 1-per-row on phone-width viewports
// (its Grid uses xs:12/sm:6/md:3) - same values/calculations, different
// layout only (checklist Section 10.4).
export default function PerformanceKpiCards({ rows }: PerformanceKpiCardsProps) {
  const impressions = sum(rows, "impressions");
  const clicks = sum(rows, "clicks");
  const spend = sum(rows, "spend");
  const leads = sum(rows, "leads");
  const qualified = sum(rows, "qualified");
  const paid = sum(rows, "paid");
  const income = sum(rows, "income");
  const profit = income - spend;
  const cpc = clicks ? spend / clicks : 0;
  const cpq = qualified ? spend / qualified : 0;

  return (
    <Grid container spacing={1.25}>
      <Grid size={6}>
        <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1, fontSize: 11 }}>
              CLICKS
            </Typography>
            <AdsClickIcon sx={{ color: PRIMARY, fontSize: 18 }} />
          </Stack>
          <Typography fontWeight={700} sx={{ mt: 0.75, fontSize: 25, color: "#1a1a1a", lineHeight: 1.1 }}>
            {formatInteger(clicks)}
          </Typography>
          <Typography sx={{ color: "#9e9e9e", fontSize: 11, display: "block", mt: 0.5 }}>
            {formatInteger(impressions)} impr - CPC {formatCurrency(cpc)}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={6}>
        <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1, fontSize: 11 }}>
              SPEND
            </Typography>
            <PaymentsIcon sx={{ color: "#E65100", fontSize: 18 }} />
          </Stack>
          <Typography fontWeight={700} sx={{ mt: 0.75, fontSize: 25, color: "#1a1a1a", lineHeight: 1.1 }}>
            {formatCurrency(spend)}
          </Typography>
          <Typography sx={{ color: "#9e9e9e", fontSize: 11, display: "block", mt: 0.5 }}>
            CPQ {formatCurrency(cpq)}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={6}>
        <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1, fontSize: 11 }}>
              LEADS
            </Typography>
            <GroupsIcon sx={{ color: PRIMARY, fontSize: 18 }} />
          </Stack>
          <Typography fontWeight={700} sx={{ mt: 0.75, fontSize: 25, color: "#1a1a1a", lineHeight: 1.1 }}>
            {formatInteger(leads)}
          </Typography>
          <Typography sx={{ color: "#9e9e9e", fontSize: 11, display: "block", mt: 0.5 }}>
            {formatInteger(qualified)} qual - {formatInteger(paid)} paid
          </Typography>
        </Paper>
      </Grid>

      <Grid size={6}>
        <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1, fontSize: 11 }}>
              PROFIT
            </Typography>
            {profit >= 0 ? (
              <TrendingUpIcon sx={{ color: POSITIVE, fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ color: NEGATIVE, fontSize: 18 }} />
            )}
          </Stack>
          <Typography
            fontWeight={700}
            sx={{ mt: 0.75, fontSize: 25, lineHeight: 1.1, color: profit > 0 ? POSITIVE : profit < 0 ? NEGATIVE : "#1a1a1a" }}
          >
            {formatCurrency(profit)}
          </Typography>
          <Typography sx={{ color: "#9e9e9e", fontSize: 11, display: "block", mt: 0.5 }}>
            income {formatCurrency(income)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}
